import { createFileRoute } from "@tanstack/react-router";
import {
  Avatar,
  Button,
  Checkbox,
  CheckboxGroup,
  FieldError,
  Form,
  Input,
  Label,
  ListBox,
  Select,
  Switch,
  TextField,
  toast,
} from "@heroui/react";
import { Controller as FormController, SubmitHandler, useForm } from "react-hook-form";
import { useState } from "react";

import {
  useCreateTariff,
  useTariffCategories,
} from "@/features/tariffs/use-tariffs.ts";
import { usePackagesPrices } from "@/features/packages-price/use-packages-prices.ts";

export const Route = createFileRoute(
  "/dashboard/categories/$categoryId/tariffs/add",
)({
  component: RouteComponent,
});

interface IFormInput {
  isActive: boolean;

  name: string;
  description: string;
  icon: number;
  image: string;
  sortIndex: number;
  tariffCategoryId: string;

  length: number;
  height: number;
  width: number;
  weight: number;

  costPriceOfAirShipment: number;
  packageId: string;
  additionalCostForFragileCargo: number;
}

function RouteComponent() {
  const { categoryId } = Route.useParams();
  const [fields, setFields] = useState<string[]>([]);

  const { mutateAsync, isPending } = useCreateTariff({
    onSuccess: async () => {
      toast.success("Успешно создано");
    },
  });

  const packagesPrices = usePackagesPrices();
  const packages = packagesPrices.data.data.map((item) => ({
    key: `${item.id}`,
    title: item.name,
    description: item.description,
    image: item.image,
  }));

  const tariffCategories = useTariffCategories();
  const categories = tariffCategories.data.data.map((item) => ({
    key: `${item.id}`,
    title: item.name,
  }));

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<IFormInput>({
    defaultValues: {
      tariffCategoryId: categoryId,
    },
  });

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    mutateAsync({
      isActive: data.isActive,
      name: data.name.trim(),
      description: data.description.trim(),
      icon: 59717,
      image: data.image,
      sortIndex: 0,
      fields,
      tariffCategoryId: Number(data.tariffCategoryId),

      length: Number(data.length),
      height: Number(data.height),
      width: Number(data.width),
      weight: Number(data.weight),

      costPriceOfAirShipment: Number(data.costPriceOfAirShipment),
      packageId: Number(data.packageId),
      additionalCostForFragileCargo: Number(data.additionalCostForFragileCargo),
    }).then(() => {
      reset();
    });
  };

  return (
    <Form className="flex flex-col pt-4 pr-4" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-row gap-2 justify-between h-12 items-center w-full">
        <p className="font-bold text-xl">Создать тариф</p>

        <Button variant="primary" isPending={isPending} type="submit">
          Сохранить
        </Button>
      </div>

      <div className="container mt-6 gap-4 flex flex-col max-w-[40rem] pb-24">
        <FormController
          control={control}
          name="isActive"
          defaultValue={true}
          render={({ field }) => (
            <Switch isSelected={field.value} onChange={field.onChange}>
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <Switch.Content>
                <Label>Активен</Label>
              </Switch.Content>
            </Switch>
          )}
        />

        <TextField isInvalid={!!errors.name?.message}>
          <Label>Название</Label>
          <Input {...register("name", { required: "Нужно заполнить поле" })} />
          <FieldError>{errors.name?.message}</FieldError>
        </TextField>

        <TextField isInvalid={!!errors.description?.message}>
          <Label>Описание</Label>
          <Input
            {...register("description", { required: "Нужно заполнить поле" })}
          />
          <FieldError>{errors.description?.message}</FieldError>
        </TextField>

        <TextField isInvalid={!!errors.image?.message}>
          <Label>Ссылка на картинку</Label>
          <Input
            {...register("image", { required: "Нужно заполнить поле" })}
          />
          <FieldError>{errors.image?.message}</FieldError>
        </TextField>

        <FormController
          control={control}
          name="tariffCategoryId"
          defaultValue={categoryId}
          rules={{ required: "Нужно заполнить поле" }}
          render={({ field }) => (
            <Select
              selectedKey={field.value ?? null}
              onSelectionChange={(key) => field.onChange(key as string)}
              isInvalid={!!errors.tariffCategoryId?.message}
            >
              <Label>Категория</Label>
              <Select.Trigger>
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <FieldError>{errors.tariffCategoryId?.message}</FieldError>
              <Select.Popover>
                <ListBox items={categories}>
                  {({ key, title }) => (
                    <ListBox.Item id={key} textValue={title}>
                      {title}
                    </ListBox.Item>
                  )}
                </ListBox>
              </Select.Popover>
            </Select>
          )}
        />

        <TextField isInvalid={!!errors.length?.message}>
          <Label>Длина (в см.)</Label>
          <Input
            defaultValue="0"
            {...register("length", { required: "Нужно заполнить поле" })}
          />
          <FieldError>{errors.length?.message}</FieldError>
        </TextField>

        <TextField isInvalid={!!errors.height?.message}>
          <Label>Высота (в см.)</Label>
          <Input
            defaultValue="0"
            {...register("height", { required: "Нужно заполнить поле" })}
          />
          <FieldError>{errors.height?.message}</FieldError>
        </TextField>

        <TextField isInvalid={!!errors.width?.message}>
          <Label>Ширина (в см.)</Label>
          <Input
            defaultValue="0"
            {...register("width", { required: "Нужно заполнить поле" })}
          />
          <FieldError>{errors.width?.message}</FieldError>
        </TextField>

        <TextField>
          <Label>Реальный вес (в кг.)</Label>
          <Input defaultValue="0" {...register("weight")} />
        </TextField>

        <TextField>
          <Label>Цена себестоимости авиа отправки за 1 кг</Label>
          <Input defaultValue="0" {...register("costPriceOfAirShipment")} />
        </TextField>

        <FormController
          control={control}
          name="packageId"
          rules={{ required: "Нужно заполнить поле" }}
          render={({ field }) => (
            <Select
              selectedKey={field.value ?? null}
              onSelectionChange={(key) => field.onChange(key as string)}
              isInvalid={!!errors.packageId?.message}
            >
              <Label>Упаковка товара в среднем за единицу</Label>
              <Select.Trigger>
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <FieldError>{errors.packageId?.message}</FieldError>
              <Select.Popover>
                <ListBox items={packages}>
                  {({ key, title, description, image }) => (
                    <ListBox.Item id={key} textValue={title}>
                      <div className="flex items-center gap-2">
                        <Avatar size="sm">
                          <Avatar.Image src={image} alt={title} />
                          <Avatar.Fallback>{title[0]}</Avatar.Fallback>
                        </Avatar>
                        <div>
                          <div>{title}</div>
                          <div className="text-xs text-neutral-500">
                            {description}
                          </div>
                        </div>
                      </div>
                    </ListBox.Item>
                  )}
                </ListBox>
              </Select.Popover>
            </Select>
          )}
        />

        <TextField>
          <Label>Доп расход на хрупкий груз</Label>
          <Input
            defaultValue="0"
            {...register("additionalCostForFragileCargo")}
          />
        </TextField>

        <div>
          <p className="text-sm font-medium mb-2">Поля доступные для заполнения</p>
          <CheckboxGroup
            value={fields}
            onChange={setFields}
          >
          <Checkbox value="WIDTH">
            <Checkbox.Control>
              <Checkbox.Indicator />
            </Checkbox.Control>
            <Checkbox.Content>Ширина</Checkbox.Content>
          </Checkbox>
          <Checkbox value="LENGTH">
            <Checkbox.Control>
              <Checkbox.Indicator />
            </Checkbox.Control>
            <Checkbox.Content>Длина</Checkbox.Content>
          </Checkbox>
          <Checkbox value="HEIGHT">
            <Checkbox.Control>
              <Checkbox.Indicator />
            </Checkbox.Control>
            <Checkbox.Content>Высота</Checkbox.Content>
          </Checkbox>
          <Checkbox value="WEIGHT">
            <Checkbox.Control>
              <Checkbox.Indicator />
            </Checkbox.Control>
            <Checkbox.Content>Вес</Checkbox.Content>
          </Checkbox>
          <Checkbox value="VOLUMETRIC_WEIGHT">
            <Checkbox.Control>
              <Checkbox.Indicator />
            </Checkbox.Control>
            <Checkbox.Content>Объемный вес</Checkbox.Content>
          </Checkbox>
          </CheckboxGroup>
        </div>
      </div>
    </Form>
  );
}
